import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
    this.#applyBestSellersPriorityOrder();
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  #applyBestSellersPriorityOrder() {
    const path = window.location.pathname.replace(/\/$/, '');
    if (path !== '/collections/best-sellers') return;

    const sortBy = new URLSearchParams(window.location.search).get('sort_by');
    if (sortBy && sortBy !== 'manual') return;

    const priorityHandles = [
      'symphony-louis-vuitton-eau-de-parfum',
      'valaya-eau-de-parfum-parfums-de-marly-paris',
      'louis-vuitton-eau-de-parfum',
      'delina-eau-de-parfum-parfums-de-marly-paris',
    ];
    const { grid } = this.refs;
    if (!grid) return;

    const items = Array.from(grid.querySelectorAll('.product-grid__item'));
    const itemsByHandle = new Map();

    for (const item of items) {
      const link = item.querySelector('a[href*="/products/"]');
      if (!(link instanceof HTMLAnchorElement)) continue;

      const handle = new URL(link.href).pathname.split('/products/')[1]?.split('/')[0];
      if (handle) itemsByHandle.set(handle, item);
    }

    const priorityItems = priorityHandles.map((handle) => itemsByHandle.get(handle)).filter(Boolean);
    if (priorityItems.length < 2) return;

    for (let i = priorityItems.length - 1; i >= 0; i--) {
      grid.prepend(priorityItems[i]);
    }

    Array.from(grid.querySelectorAll('.product-grid__item')).forEach((item, index) => {
      for (const className of Array.from(item.classList)) {
        if (className.startsWith('product-grid__item--')) item.classList.remove(className);
      }
      item.classList.add(`product-grid__item--${index}`);
    });
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
